import React, {Fragment, useEffect} from 'react';
import DOMPurify from 'dompurify';
import {useDataContext} from '../context/DataContext';
import {formatCurrency} from '../lib/helpers';

const skeleton = [];
for (let i = 0; i < 3; i++) {
  skeleton.push(
    <div key={i} className="housing-types-list__row">
      <div className="housing-types-list__cell housing-types-list__cell--visual">
        <div>
          <div className="housing-types-list__visual">
            <figure className="visual ratio--4x3 housing-type-skeleton" />
          </div>
          <div className="housing-types-list__number">#</div>
        </div>
      </div>
      <div className="housing-types-list__cell-group">
        <div className="order-2--lt-desk housing-types-list__cell-group--block">
          <div className="housing-types-list__cell housing-types-list__cell--inline housing-types-list__kind" style={{width: '100%'}}>
            <span className="housing-type-skeleton">&nbsp;</span>
          </div>
          <div className="housing-types-list__cell housing-types-list__cell--inline housing-types-list__variant">
            <span className="housing-type-skeleton">&nbsp;</span>
          </div>
        </div>
        <div className="order-4--lt-desk housing-types-list__cell-group housing-types-list__cell-group--row">
          <div className="housing-types-list__cell unit-6-12--lt-desk housing-types-list__metric-living">
            <span className="housing-type-skeleton">&nbsp;</span>
          </div>
          <div className="housing-types-list__cell unit-6-12--lt-desk housing-types-list__metric-parcel">
            <span className="housing-type-skeleton">&nbsp;</span>
          </div>
        </div>
        <div className="order-3--lt-desk housing-types-list__cell housing-types-list__price">
          <span className="housing-type-skeleton" style={{width: '150px'}}>&nbsp;</span>
        </div>
        <div className="housing-types-list__cell order-1--lt-desk housing-types-list__status">
          <span className="housing-type-skeleton">&nbsp;</span>
        </div>
      </div>
    </div>
  );
}

const List = (props) => {
  // eslint-disable-next-line react/prop-types
  const {showResults} = props;

  const {
    isLoading,
    filteredConstructionNumbers,
    activeFiltersCount,
    listItemsCount,
    setListItemsCount,
    sortOptions,
    sortBy,
    setSortBy,
    subProjectStatus,
    hidePrices,
    isRental,
    subProject,
    isDisabled
  } = useDataContext();

  useEffect(() => {
    const rootElement = document.querySelector('.pimcore_area_construction-number-table');

    if (!filteredConstructionNumbers || !rootElement) return;

    if (filteredConstructionNumbers.length === 0 && rootElement && isRental) {
      rootElement.classList.add('visuallyhidden');
    } else {
      rootElement.classList.remove('visuallyhidden');
    }
  }, [filteredConstructionNumbers, isRental]);

  const renderPrice = (prices) => {
    /** price render for rental (used on Borgdonck) */
    if (isRental && prices.rentPrice) {
      return (
        <>
          {formatCurrency(prices.rentPrice)}
          &nbsp;<span className="housing-types-list__price--suffix">per maand</span>
        </>
      );
    }

    /** price render when a definitive price is given */
    if (!isRental && prices.definitive) {
      return (
        <>
          {formatCurrency(prices.definitive)}
          &nbsp;<span className="housing-types-list__price--suffix">v.o.n.</span>
        </>
      );
    }

    /** price render when when a indicative price is given */
    if (!isRental && !prices.definitive && prices.indicative) {
      return (
        <>
          <span className="housing-types-list__price--prefix">vanaf</span>
          &nbsp;{formatCurrency(prices.indicative)}
          &nbsp;<span className="housing-types-list__price--suffix">v.o.n. indicatief</span>
        </>
      );
    }

    return 'Binnenkort bekend';
  };

  // eslint-disable-next-line consistent-return
  const routeHelper = (item) => (isRental ? `${subProject.subprojectNameUrlFriendly}/woningtypes/bouwnummer-${item.number}` : `woningtypes/bouwnummer-${item.number}`);

  /** todo: check if these states are correct */
  const convertState = (status) => {
    switch (status) {
      case 'Verhuurd':
      case 'Verkocht':
        return 'sold';
      case 'In optie':
        return 'in-option';
      case 'Beschikbaar':
      case 'Te huur':
        return 'available';
      default:
        return '';
    }
  };

  /** render function for the address button, would prefer to render all buttons this way as it's more readable. */
  const renderAddressButton = () => {
    if (isRental) {
      return (
        <div className="housing-types-list__cell">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
          <button
            type="button"
            className={`button-sort ${sortBy === 'addressAsc' && 'button-sort--asc'} ${sortBy === 'addressDesc' && 'button-sort--desc'}`}
            onClick={() => setSortBy(sortBy === 'addressAsc' ? 'addressDesc' : 'addressAsc')}
          >
            Adres
          </button>
        </div>
      );
    }
  };

  /** render function for the address cell, would prefer to render all buttons this way as it's more readable. */
  const renderAddressCell = (item) => {
    if (isRental) {
      return (
        <div className="housing-types-list__cell housing-types-list__cell--inline housing-types-list__variant">
          {item.address ? item.address : ''}
        </div>
      );
    }
  };

  const renderResults = () => {
    if (showResults) {
      return (
        <div className="housing-types-list__results grid">
          <div className="grid__cell unit-6-12 text-small">
            <strong>{filteredConstructionNumbers.length}</strong> {filteredConstructionNumbers.length < 2 ? 'resultaat' : 'resultaten'}
          </div>
          <div className="grid__cell unit-6-12 text-small text-right text-small visuallyhidden--desk">
            {activeFiltersCount} filter{activeFiltersCount !== 1 && 's'} actief
          </div>
        </div>
      );
    }
  };

  const constructionNumberClass = () => {
    if (isDisabled) {
      return 'housing-types-list__row link-clean link-clean-color is--disabled';
    }

    return 'housing-types-list__row link-clean link-clean-color';
  };

  const spaceLabel = () => (isRental ? 'Woonopp.' : 'Gebruiksopp.');

  return (
    <div className={filteredConstructionNumbers.length > listItemsCount ? '' : 'sign'}>
      <h1>{showResults}</h1>
      {renderResults()}
      <div className="text-small text-right sign visuallyhidden--desk">
        Sorteer op
        <select
          name="sortBy"
          onChange={(e) => setSortBy(e.target.value)}
          style={{display: 'inline-block', width: 'auto', marginLeft: '10px'}}
          value={sortBy}
        >
          {Object.entries(sortOptions).map(([key, label]) => (
            <option value={key} key={key}>{label}</option>
          ))}
        </select>
      </div>
      {!isLoading && filteredConstructionNumbers.length === 0 ? (
        <p>
          Er is helaas geen aanbod wat voldoet aan je zoekcriteria. Wijzig je zoekcriteria om geschikt
          woningaanbod te vinden.
        </p>
      ) : (
        <div className="housing-types-list__wrapper">
          <div className="housing-types-list__container">
            <div className="housing-types-list__row housing-types-list__row--head visuallyhidden--lt-desk">
              <div className="housing-types-list__cell" />
              <div className="housing-types-list__cell">
                <button
                  type="button"
                  className={`button-sort ${sortBy === 'housingKindAsc' && 'button-sort--asc'} ${sortBy === 'housingKindDesc' && 'button-sort--desc'}`}
                  onClick={() => setSortBy(sortBy === 'housingKindAsc' ? 'housingKindDesc' : 'housingKindAsc')}
                >
                  Soort
                </button>
              </div>
              <div className="housing-types-list__cell">
                <button
                  type="button"
                  className={`button-sort ${sortBy === 'typeAsc' && 'button-sort--asc'} ${sortBy === 'typeDesc' && 'button-sort--desc'}`}
                  onClick={() => setSortBy(sortBy === 'typeAsc' ? 'typeDesc' : 'typeAsc')}
                >
                  Type
                </button>
              </div>
              {renderAddressButton()}
              <div className="housing-types-list__cell">
                <button
                  type="button"
                  className={`button-sort ${sortBy === 'metricsLiningAsc' && 'button-sort--asc'} ${sortBy === 'metricsLiningDesc' && 'button-sort--desc'}`}
                  onClick={() => setSortBy(sortBy === 'metricsLiningAsc' ? 'metricsLiningDesc' : 'metricsLiningAsc')}
                >
                  {spaceLabel()}
                </button>
              </div>
              <div className="housing-types-list__cell">
                <button
                  type="button"
                  className={`button-sort ${sortBy === 'metricsLotAsc' && 'button-sort--asc'} ${sortBy === 'metricsLotDesc' && 'button-sort--desc'}`}
                  onClick={() => setSortBy(sortBy === 'metricsLotAsc' ? 'metricsLotDesc' : 'metricsLotAsc')}
                >
                  Perceelopp.
                </button>
              </div>
              <div className="housing-types-list__cell">
                <button
                  type="button"
                  className={`button-sort ${sortBy === 'priceAsc' && 'button-sort--asc'} ${sortBy === 'priceDesc' && 'button-sort--desc'}`}
                  onClick={() => setSortBy(sortBy === 'priceAsc' ? 'priceDesc' : 'priceAsc')}
                >
                  Prijs
                </button>
              </div>
              <div className="housing-types-list__cell">
                Status
              </div>
            </div>
            {isLoading ? (
              skeleton
            ) : (
              filteredConstructionNumbers.slice(0, listItemsCount).map((item) => (
                <a
                  className={constructionNumberClass()}
                  key={item.id}
                  href={routeHelper(item)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="housing-types-list__cell housing-types-list__cell--visual">
                    <div>
                      <div className="housing-types-list__visual">
                        <figure className="visual ratio--4x3">
                          <picture
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(item.image)
                            }}
                          />
                        </figure>
                      </div>
                      <div className="housing-types-list__number">{item.number}</div>
                    </div>
                  </div>
                  <div className="housing-types-list__cell-group padding-left-2">
                    <div className="order-2--lt-desk housing-types-list__cell-group--block">
                      <div className="housing-types-list__cell housing-types-list__cell--inline housing-types-list__kind">
                        {item.kind}
                      </div>
                      <div className="housing-types-list__cell housing-types-list__cell--inline housing-types-list__type">
                        {item.housingVariant}
                      </div>
                      {renderAddressCell(item)}
                    </div>
                    <div className="order-4--lt-desk housing-types-list__cell-group housing-types-list__cell-group--row">
                      <div className="housing-types-list__cell unit-6-12--lt-desk housing-types-list__metric-living">
                        {item.metrics.living && <>{item.metrics.living} m<sup>2</sup></>}
                      </div>
                      <div className="housing-types-list__cell unit-6-12--lt-desk housing-types-list__metric-parcel">
                        {item.metrics.parcel && <>{item.metrics.parcel} m<sup>2</sup></>}
                      </div>
                    </div>
                    <div className="order-3--lt-desk housing-types-list__cell housing-types-list__price sign-s--palm">
                      {item.state === 'Verkocht' || item.state === 'Verhuurd' || (subProjectStatus === 'Voorinschrijving' && hidePrices) ? '-' : renderPrice(item.prices, item)}
                    </div>
                    <div className={`housing-types-list__cell order-1--lt-desk housing-types-list__status status--${item.state}`}>
                                            <span className={`label ${isRental ? 'label--tag' : ''} tag--status--${convertState(item.state)}`}>
                                                {item.state}
                                            </span>
                    </div>
                  </div>
                </a>
              ))
            )}
          </div>
          {filteredConstructionNumbers.length > listItemsCount && (
            <div className="text-center layer-s">
              <button
                type="button"
                className="button button--secondary button--no-arrow"
                onClick={() => setListItemsCount((prevState) => prevState + 10)}
              >
                Toon meer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default List;
